import { memo } from 'react';
import { Box } from '@mantine/core';
import { FormSectionProps } from '../../../types/forms';
import classes from './FormSection.module.css';

/**
 * Reusable FormSection component that provides consistent section styling
 * for forms.
 *
 * Features:
 * - Section headers with consistent styling
 * - Icon support
 * - Description text support
 * - Error state display
 * - Required field indicators
 *
 * For stacked collapsible groups use a Mantine `Accordion` / `Collapse`-based
 * card instead (e.g. the share form's `ArtifactShareCard`) — FormSection is a
 * flex-fill section, not an accordion item.
 *
 * @example
 * ```tsx
 * <FormSection
 *   title="Task Details"
 *   description="Enter the basic information for your task"
 *   icon={<IconClipboard size={20} />}
 * >
 *   <FormField type="text" label="Title" value={title} onChange={setTitle} />
 *   <FormField type="textarea" label="Description" value={desc} onChange={setDesc} />
 * </FormSection>
 * ```
 */
export const FormSection = memo(function FormSection({
  title,
  description,
  children,
  icon,
  required = false,
  error,
  className = '',
}: FormSectionProps) {
  const containerClasses = [
    classes.sectionContainer,
    error ? classes.sectionContainerError : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const titleClasses = [
    classes.sectionTitle,
    required ? classes.sectionTitleRequired : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Render the header only when there is something to show. A headerless
  // FormSection becomes a pure flex-fill layout wrapper (no empty <h3>).
  const hasHeader = Boolean(title || description || icon || error);

  return (
    <Box className={containerClasses}>
      {hasHeader && (
        <div className={classes.sectionHeader}>
          <div className={classes.sectionHeaderContent}>
            {icon && <div className={classes.sectionIcon}>{icon}</div>}

            <div className={classes.sectionTitleArea}>
              {title && <h3 className={titleClasses}>{title}</h3>}

              {description && (
                <p className={classes.sectionDescription}>{description}</p>
              )}

              {error && <div className={classes.sectionError}>{error}</div>}
            </div>
          </div>
        </div>
      )}

      <div className={classes.sectionContent}>{children}</div>
    </Box>
  );
});
